import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "./api";

//tokens are saved in cookies.

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const getCookie = (name) => {
    const cookies = document.cookie.split("; ");
    for (let cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split("=");
      if (decodeURIComponent(cookieName) === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  };

  const [token, setToken] = useState(() => getCookie("auth_token") || null);
  const [userDetails, setUserDetails] = useState();
  const [userLevel, setUserLevel] = useState(false);

  const navigate = useNavigate();
  const { auth_token } = useParams();
  useEffect(() => {
    const fetchLevel = async () => {
      if (token) {
        try {
          const response = await api.get(`/user/level`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          setUserLevel(response.data.user_level);
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      }
    };

    fetchLevel();
  }, [token]);
  useEffect(() => {
    const verifyToken = async () => {
      if (token && userLevel === "premium") {
        try {
          const response = await api.get(`/user/token/expiry`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.data.token) {
            setToken(response.data.token);
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            removeCookie("auth_token");
            navigate("/signin");
          } else {
            console.error("Error during token verification:", error);
          }
        }
      }
    };

    verifyToken();
  }, [token, navigate, auth_token]);

  useEffect(() => {
    if (token) {
      setCookie("auth_token", token, { path: "/" });
    } else {
      removeCookie("auth_token");
    }
  }, [token]);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await api.get(`/user/details`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setUserDetails(response.data);
          sessionStorage.setItem("user", JSON.stringify(response.data));
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      }
    };

    fetchUser();
  }, [token]);

  const deleteNewThreads = async () => {
    if (!token) return;

    try {
      await api.delete("/chat/history/thread/delete/?all_new_thread=true", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("New threads cleaned up.");
    } catch (error) {
      console.error("Error deleting new threads:", error);
    } finally {
      // setCleanupDone(true);
    }
  };

  const login = async (newToken) => {
    await deleteNewThreads();
    setToken(newToken);
    setCookie("auth_token", newToken, { path: "/" });
    setTimeout(() => {
      navigate("/app");
    }, 1000);
  };

  const logout = async () => {
    const root = document.documentElement;

    setToken(null);
    removeCookie("auth_token");
    root.classList.remove("dark");
    root.style.colorScheme = "light";
    localStorage.clear();
    sessionStorage.clear();
    await deleteNewThreads();
    navigate("/signin");
  };

  const setCookie = (name, value, options = {}) => {
    options = {
      ...options,
      sameSite: "strict",
    };

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(
      value,
    )}`;

    for (let optionKey in options) {
      let optionValue = options[optionKey];
      cookieString += `; ${optionKey}`;
      if (optionValue !== true) {
        cookieString += `=${optionValue}`;
      }
    }

    document.cookie = cookieString;
  };

  const removeCookie = (name) => {
    document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; path=/`;
  };

  return (
    <AuthContext.Provider
      value={{ token, login, logout, userDetails, userLevel }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
